import { zodResolver } from '@hookform/resolvers/zod';
import { AppError } from '@repo/error';
import { errorMessage, t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Badge } from '@repo/ui/badge';
import { Button } from '@repo/ui/button';
import { Card, CardContent } from '@repo/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Textarea } from '@repo/ui/textarea';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { RiskBadge } from '@/components/raven/shared';
import { trpc } from '@/providers/trpc';
import type { RouterOutputs } from '@/providers/trpc-client';

const decisionSchema = z.object({
  rationale: z.string().trim().min(1, t('extra.explainYourDecision')).max(4000),
  acknowledgedHighRisk: z.boolean(),
});
export const ProfileDecisionSection = ({
  reportDetail: report,
}: {
  reportDetail: RouterOutputs['report']['getReport'];
}) => {
  useLocale();

  const utils = trpc.useUtils();
  const form = useForm<z.infer<typeof decisionSchema>>({
    resolver: zodResolver(decisionSchema, { error: validationError }),
    defaultValues: {
      rationale: '',
      acknowledgedHighRisk: false,
    },
  });
  const decision = trpc.report.submitReportDecision.useMutation({
    onSuccess: () => {
      toast.success(t('auth.decisionRecorded'));
      void utils.report.getReport.invalidate({ reportId: report._id });
      void utils.report.listCases.invalidate();
      void utils.workspace.invalidate();
    },
    onError: (cause) => {
      const error = AppError.parse(cause);
      toast.error(errorMessage(AppError.parse(error)));
    },
  });
  const isFinal =
    report.decision?.outcome === 'ACCEPTED' || report.decision?.outcome === 'REJECTED';
  const hasIncompleteResearch = !(
    report.assessment?.requiredChecksComplete ?? report.assessment?.coverageComplete
  );
  const acknowledgedHighRisk = form.watch('acknowledgedHighRisk');
  const submit = (outcome: 'ACCEPTED' | 'REJECTED') =>
    form.handleSubmit((data) =>
      decision.mutate({
        reportId: report._id,
        expectedRevision: report.revision,
        outcome,
        ...data,
      }),
    )();
  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <h2 className="font-semibold text-xl">{t('auth.officerDecision')}</h2>
        <div className="flex flex-wrap gap-2">
          <RiskBadge level={report.riskLevel} />
          {report.assessment && !report.assessment.reviewComplete && (
            <Badge variant="outline">{t('auth.provisionalReviewRequired')}</Badge>
          )}
          {hasIncompleteResearch && <Badge variant="outline">{t('auth.researchIncomplete')}</Badge>}
          {report.assessment?.reviewComplete && (
            <Badge variant="outline">{t('auth.allFindingsReviewed')}</Badge>
          )}
          <Badge variant="outline">{t('auth.webResearch')}</Badge>
        </div>
        <p className="text-sm">
          {report.riskSummary ?? t('extra.researchHasNotProducedAnAssessmentYet')}
        </p>
        {isFinal ? (
          <div className="space-y-2 rounded border p-4">
            <p className="font-medium">
              {report.decision?.outcome === 'ACCEPTED'
                ? t('auth.clientAccepted')
                : t('auth.clientRejected')}{' '}
              {t('auth.reportFinalized')}{' '}
            </p>
            <p className="text-sm">{report.decision?.rationale}</p>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={(event) => event.preventDefault()}>
              <fieldset className="space-y-4" disabled={decision.isPending}>
                <FormField
                  control={form.control}
                  name="rationale"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.decisionRationale')}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder={t('auth.explainWhyYouAcceptOrRejectThisClient')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {report.riskLevel === 'HIGH' && (
                  <FormField
                    control={form.control}
                    name="acknowledgedHighRisk"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <FormControl>
                            <input
                              checked={field.value}
                              onChange={(event) => field.onChange(event.target.checked)}
                              type="checkbox"
                            />
                          </FormControl>
                          {t('auth.iAcknowledgeTheHighRiskAndAcceptResponsibilityFor')}{' '}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                )}
                {!report.actions.canAccept && (
                  <p className="text-muted-foreground text-sm">
                    {t('auth.acceptanceRequiresReviewedFindings')}{' '}
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    disabled={
                      !report.actions.canAccept ||
                      decision.isPending ||
                      (report.riskLevel === 'HIGH' && !acknowledgedHighRisk)
                    }
                    onClick={() => void submit('ACCEPTED')}
                    type="button"
                  >
                    {t('auth.acceptClient')}{' '}
                  </Button>
                  <Button
                    disabled={!report.actions.canReject || decision.isPending}
                    onClick={() => void submit('REJECTED')}
                    type="button"
                    variant="destructive"
                  >
                    {t('auth.rejectClient')}{' '}
                  </Button>
                </div>
              </fieldset>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  );
};
